import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { API } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { FileDown, FileSpreadsheet, FileText, TrendingUp, TrendingDown, Wallet, Calendar } from "lucide-react";

export default function Reports() {
  const { companyId, currency, company } = useApp();
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);

  const load = () => {
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    api.get(`/companies/${companyId}/dashboard`, { params }).then(({ data }) => setData(data));
  };
  useEffect(() => { if (companyId) load(); }, [companyId, range]);

  const download = async (format) => {
    try {
      const params = new URLSearchParams({ format });
      if (range.start) params.append("start", range.start);
      if (range.end) params.append("end", range.end);
      const res = await api.get(`/companies/${companyId}/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `syncpe-report.${format === "xlsx" ? "xlsx" : format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); }
  };

  const fmt = (v) => formatMoney(v, currency);
  const fmtC = (v) => formatMoney(v, currency, true);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {company?.logo_url && (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${company.logo_url}`} alt="" className="w-11 h-11 rounded-xl object-cover border border-slate-200" data-testid="reports-logo" />
          )}
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900">Reports</h1>
            <p className="text-slate-500 text-sm mt-0.5">Financial summary & exports</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="export-csv" onClick={() => download("csv")} className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white font-semibold text-sm flex items-center gap-2 hover:bg-slate-50"><FileDown size={16} /> CSV</button>
          <button data-testid="export-xlsx" onClick={() => download("xlsx")} className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white font-semibold text-sm flex items-center gap-2 hover:bg-slate-50"><FileSpreadsheet size={16} /> Excel</button>
          <button data-testid="export-pdf" onClick={() => download("pdf")} className="px-3 py-2.5 rounded-xl bg-indigo-600 text-white font-semibold text-sm flex items-center gap-2 hover:bg-indigo-700"><FileText size={16} /> PDF</button>
        </div>
      </div>

      {/* Date range */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <Calendar size={18} className="text-slate-400" />
        <span className="text-sm font-semibold text-slate-600">Date range</span>
        <input type="date" data-testid="report-start" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="px-3 py-2 rounded-xl border border-slate-200 text-sm" />
        <span className="text-slate-400">to</span>
        <input type="date" data-testid="report-end" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="px-3 py-2 rounded-xl border border-slate-200 text-sm" />
        {(range.start || range.end) && <button onClick={() => setRange({ start: "", end: "" })} className="text-sm text-indigo-600 font-semibold">Clear</button>}
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Summary icon={TrendingUp} label="Total Income" value={fmt(data.total_in)} color="text-emerald-600" tint="bg-emerald-50 text-emerald-600" />
            <Summary icon={TrendingDown} label="Total Expenses" value={fmt(data.total_out)} color="text-rose-600" tint="bg-rose-50 text-rose-600" />
            <Summary icon={Wallet} label="Net Position" value={fmt(data.net)} color="text-slate-900" tint="bg-indigo-50 text-indigo-600" />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-heading font-bold text-slate-800 mb-4">Monthly Financial Summary</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.monthly} margin={{ left: -10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickFormatter={fmtC} width={70} />
                <Tooltip cursor={{ fill: "#F8FAFC" }} formatter={(v) => fmt(v)} />
                <Bar dataKey="net" name="Net" radius={[6, 6, 0, 0]}>
                  {data.monthly.map((m, i) => <Cell key={i} fill={m.net >= 0 ? "#10B981" : "#EF4444"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-heading font-bold text-slate-800 mb-4">Category-wise Spending</h3>
              <div className="space-y-2">
                {data.categories.slice(0, 10).map((c, i) => {
                  const pct = data.total_out ? (c.value / data.total_out) * 100 : 0;
                  return (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-slate-700">{c.name}</span>
                        <span className="font-bold text-slate-900">{fmt(c.value)}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                {data.categories.length === 0 && <p className="text-slate-400 text-sm py-4 text-center">No data</p>}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-heading font-bold text-slate-800 mb-4">Bank / Account Report</h3>
              <div className="space-y-3">
                {data.accounts.map((a, i) => (
                  <div key={i} className="p-3 rounded-xl bg-slate-50">
                    <div className="flex justify-between items-center">
                      <p className="font-semibold text-slate-800 text-sm">{a.name}</p>
                      <p className="font-bold text-slate-900">{fmt(a.balance)}</p>
                    </div>
                    <div className="flex gap-4 mt-1 text-xs">
                      <span className="text-emerald-600">In {fmt(a.in)}</span>
                      <span className="text-rose-600">Out {fmt(a.out)}</span>
                    </div>
                  </div>
                ))}
                {data.accounts.length === 0 && <p className="text-slate-400 text-sm py-4 text-center">No accounts</p>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Summary({ icon: Icon, label, value, color, tint }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className={`w-10 h-10 rounded-xl grid place-items-center ${tint}`}><Icon size={20} /></div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mt-4">{label}</p>
      <p className={`text-2xl font-heading font-extrabold mt-1 ${color}`}>{value}</p>
    </div>
  );
}
